import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Hash, X, Plus, Tags } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

import { TL } from "@/lib/prekladyUI";
const PRESET_SUGGESTIONS = [
  "vip", "osvc", "rodic", "doktor", "podnikatel", "rezident",
  "pronajem", "mercedes", "bmw", "skoda", "audi", "porsche",
  "sportovec", "investor", "manager", "ucitel", "umelec",
];

/**
 * TagsEditor — full-text hashtag manager for a contact.
 * - Displays tags as cognac pills, click X to remove
 * - Input with auto-suggest from presets + Enter to add
 * - Saves via PUT /api/crm/contacts/{id} { tags }
 * - Calls onChange(tags) so parent stays in sync
 */
export default function TagsEditor({ contactId, initialTags = [], onChange }) {
  const [tags, setTags] = useState(() => normalizeTags(initialTags));
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);

  const persist = async (next) => {
    setSaving(true);
    try {
      await api.put(`/api/crm/contacts/${contactId}`, { tags: next });
      onChange?.(next);
    } catch (e) {
      toast.error("Tag se nepodařilo uložit");
      // rollback
      setTags(tags);
    } finally {
      setSaving(false);
    }
  };

  const addTag = (raw) => {
    const clean = cleanOne(raw);
    if (!clean) return;
    if (tags.includes(clean)) {
      toast.info(`#${clean} už je přidán`);
      return;
    }
    const next = [...tags, clean];
    setTags(next);
    setInput("");
    persist(next);
  };

  const removeTag = (t) => {
    const next = tags.filter((x) => x !== t);
    setTags(next);
    persist(next);
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(input);
    } else if (e.key === "Backspace" && !input && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const presetsRemaining = PRESET_SUGGESTIONS.filter((s) => !tags.includes(s)).slice(0, 6);

  return (
    <div data-testid="tags-editor">
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]" data-testid="tags-list">
        <AnimatePresence>
          {tags.map((t) => (
            <motion.span
              key={t}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.85 }}
              transition={{ duration: 0.15 }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-drobny font-medium"
              style={{
                background: "rgba(194,89,52,0.10)",
                color: "#9C4326",
                border: "1px solid rgba(194,89,52,0.30)",
              }}
              data-testid={`tag-pill-${t}`}
            >
              <Hash size={10} strokeWidth={2.4} />
              {t}
              <button
                onClick={() => removeTag(t)}
                className="ml-0.5 hover:bg-black/10 rounded-full p-0.5 transition-colors"
                data-testid={`tag-remove-${t}`}
                aria-label={`Smazat tag ${t}`}
              >
                <X size={9} strokeWidth={2.4} />
              </button>
            </motion.span>
          ))}
        </AnimatePresence>
        {tags.length === 0 && (
          <span className="text-drobny italic" style={{ color: "#A3A3A3" }}>
            Zatím žádné hashtagy. Přidej rychlou identifikaci klienta — #vip, #mercedesgle, #osvc.
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center gap-1 px-2.5 py-1.5 rounded-md" style={{ background: "var(--surface)", border: "1px solid rgba(0,0,0,0.10)" }}>
          <Hash size={12} style={{ color: "#A3A3A3" }} />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder="napiš tag a Enter (např. mercedesgle)"
            data-testid="tag-input"
            className="flex-1 bg-transparent outline-none text-sm tabular-nums"
            style={{ color: "var(--ink)" }}
            maxLength={24}
          />
        </div>
        <button aria-label={TL("Přidat štítek")}
          onClick={() => addTag(input)}
          disabled={!input.trim() || saving}
          data-testid="tag-add-btn"
          className="px-3 py-1.5 rounded-md text-xs font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            background: "linear-gradient(135deg, #C25934 0%, #4A3F35 100%)",
            color: "#FFF",
            boxShadow: "0 2px 8px rgba(194,89,52,0.25)",
          }}
        >
          <Plus size={13} className="inline -mt-0.5" />
        </button>
      </div>

      {presetsRemaining.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          <span className="text-popisek uppercase tracking-wider font-semibold flex items-center gap-1" style={{ color: "#A3A3A3" }}>
            <Tags size={9} /> Návrhy
          </span>
          {presetsRemaining.map((s) => (
            <button
              key={s}
              onClick={() => addTag(s)}
              data-testid={`tag-preset-${s}`}
              className="text-drobny px-1.5 py-0.5 rounded-full transition-colors hover:bg-black/10"
              style={{ color: "#737373", border: "1px dashed rgba(0,0,0,0.15)" }}
            >
              #{s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function cleanOne(raw) {
  if (!raw) return "";
  let s = String(raw).trim().toLowerCase().replace(/^#+/, "");
  // Strip diacritics (Czech)
  s = s.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
  // Keep alphanumeric only
  s = s.replace(/[^a-z0-9]/g, "");
  return s.slice(0, 24);
}

function normalizeTags(arr) {
  if (!Array.isArray(arr)) return [];
  const out = [];
  const seen = new Set();
  for (const x of arr) {
    const c = cleanOne(x);
    if (c && !seen.has(c)) {
      seen.add(c);
      out.push(c);
    }
  }
  return out.slice(0, 30);
}
